import { AppIcon } from '@/components/ui/app-icon';
import {
  PRODUCTHUNT_UPVOTE_CREDITS,
  ProductHuntRewardDialog,
} from '@/components/producthunt-reward-dialog.component';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { iconArrowRight, iconArrowUp, iconXmark } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useGetMyProductHuntCreditsQuery } from '@/store/features/producthunt-credits.feature';
import { useUser } from '@clerk/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEffect, useState } from 'react';

/** Per-user dismissal, TTL-limited so a still-unclaimed reward resurfaces. */
const DISMISS_KEY_PREFIX = 'cl_producthunt_reward_dismissed_';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function readDismissed(userId: string): boolean {
  try {
    const raw = localStorage.getItem(`${DISMISS_KEY_PREFIX}${userId}`);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function writeDismissed(userId: string) {
  try {
    localStorage.setItem(`${DISMISS_KEY_PREFIX}${userId}`, String(Date.now()));
  } catch {
    // Storage unavailable (private mode) — dismissal just won't persist.
  }
}

/**
 * Full-width bar above the page card offering NON-AppSumo orgs credits for
 * supporting our Product Hunt launch (200 for an upvote, 300 for a comment).
 *
 * Operationally gated on the PostHog flag `producthunt_credits_v1` (the same key
 * the backend gates on) AND explicitly hidden for AppSumo orgs — they get the
 * AppSumo review-reward bar instead, so the two never both show. Hidden once
 * both rewards are approved; a compact confirmation shows while a submission is
 * pending.
 */
export function ProductHuntRewardBanner() {
  const { user } = useUser();
  const userId = user?.id;
  const enabled = useFeatureFlagEnabled('producthunt_credits_v1') === true;
  const { isAppSumo, isReady } = useFeatureAccess();

  // The non-AppSumo target: flag on, feature access resolved, not an AppSumo org.
  const eligible = enabled && isReady && !isAppSumo;

  const { data } = useGetMyProductHuntCreditsQuery(undefined, {
    skip: !eligible,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (userId) setDismissed(readDismissed(userId));
  }, [userId]);

  const upvoteDone = data?.upvote?.status === 'approved';
  const anyPending = data?.upvote?.status === 'pending';

  // Credits still up for grabs (upvote is the only reward).
  const remaining = upvoteDone ? 0 : PRODUCTHUNT_UPVOTE_CREDITS;

  // Nothing to offer: not eligible, reward claimed, or dismissed.
  if (!eligible || remaining === 0 || dismissed) return null;

  const handleDismiss = () => {
    if (userId) writeDismissed(userId);
    setDismissed(true);
  };

  return (
    <>
      <div
        role='region'
        aria-label='Product Hunt launch reward'
        className={cn(
          'flex h-10 items-center gap-3 border-b px-4',
          'border-orange-200 bg-orange-50',
          'dark:border-orange-500/20 dark:bg-orange-500/10'
        )}
      >
        <AppIcon
          icon={iconArrowUp}
          className='h-4 w-4 shrink-0 text-orange-500'
          style={{ fontSize: 14 }}
        />

        <p className='truncate text-[13px] text-orange-800 dark:text-orange-200'>
          <span className='font-semibold'>
            Get {remaining.toLocaleString()} free credits
          </span>
          <span className='hidden sm:inline'>
            {' '}
            — upvote us on Product Hunt to support our launch.
          </span>
          {anyPending && (
            <span className='hidden font-medium md:inline'>
              {' '}
              (a submission is pending review)
            </span>
          )}
        </p>

        <div className='ml-auto flex shrink-0 items-center gap-2'>
          <button
            type='button'
            onClick={() => setDialogOpen(true)}
            className='inline-flex h-7 items-center gap-1 rounded-md bg-orange-500 px-3 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-orange-500/90 focus-visible:ring-1 focus-visible:ring-orange-500/60 focus-visible:outline-hidden'
          >
            Earn credits
            <AppIcon icon={iconArrowRight} className='h-3 w-3' />
          </button>
          <button
            type='button'
            onClick={handleDismiss}
            aria-label='Dismiss Product Hunt reward banner'
            className='rounded-md p-1 text-orange-700/70 transition-colors hover:bg-orange-500/15 hover:text-orange-800 focus-visible:ring-1 focus-visible:ring-orange-500/60 focus-visible:outline-hidden dark:text-orange-300/70 dark:hover:text-orange-200'
          >
            <AppIcon icon={iconXmark} className='h-3.5 w-3.5' />
          </button>
        </div>
      </div>

      <ProductHuntRewardDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
